import React, { memo, useEffect, useMemo } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { TextField, Typography } from "@mui/material";
import { sanitizeNumber, formatWithCommas } from "./MatrixComplianceUtils";

// ── Id for the synthetic, always-first "Sendout" totals row ────────────────
const SENDOUT_ROW_ID = "__sendout__";

// ── Formats a numeric sendout value with thousands separators ──────────────
const formatSendout = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString("en-US") : value;
};

// ── Darker text for disabled/read-only fields ──────────────────────────────
// MUI's default disabled color is a light gray. WebkitTextFillColor is
// included because Chrome/Safari use it (not `color`) to paint disabled
// and readOnly text inputs, so both need to be forced with !important.
const disabledFieldSx = {
  "& .Mui-disabled": {
    color: "#1a3a5c !important",
    WebkitTextFillColor: "#1a3a5c !important",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#1a3a5c !important",
    WebkitTextFillColor: "#1a3a5c !important",
  },
};

const sharedTableOptions = {
  enableSorting: false,
  enableColumnFilters: false,
  enableFullScreenToggle: false,
  enableColumnActions: false,
  enableColumnOrdering: false,
  enablePagination: false,
  enableGlobalFilter: false,
  enableDensityToggle: false,
  enableTopToolbar: false,
  enableRowActions: false,
  enableRowSelection: false,
  enableHiding: false,
  enableStickyHeader: true,
  muiTableHeadCellProps: {
    sx: {
      backgroundColor: "#1a3a5c",
      color: "#fff",
      fontWeight: 700,
      textAlign: "center",
      borderRight: "1px solid rgba(255,255,255,0.12)",
      py: 1.5,
      "& .Mui-TableHeadCell-Content": {
        justifyContent: "center",
      },
    },
  },
  muiTableBodyCellProps: {
    sx: {
      borderRight: "1px solid #e0e7ef",
      py: 0.75,
      px: 1,
      "&:last-child": { borderRight: "none" },
    },
  },
  muiTablePaperProps: {
    elevation: 0,
    sx: {
      border: "1px solid #e0e7ef",
      borderRadius: "10px",
      overflow: "hidden",
      mt: 2,
    },
  },
  muiTableContainerProps: {
    sx: { maxHeight: 420, overflowX: "auto", overflowY: "auto" },
  },
};

// ── Memoized so an edit in one cell doesn't re-render every other cell ─────
// `onChange` here must be a stable (useCallback'd) reference and rowKey/colKey
// must be primitives — otherwise React.memo's shallow prop check always sees
// "changed" props and this memoization does nothing.
const EditableCell = memo(function EditableCell({ value, canEdit, rowKey, colKey, onChange }) {
  const handleChange = (e) => {
    if (canEdit) onChange(rowKey, colKey, sanitizeNumber(e.target.value));
  };

  return (
    <TextField
      size="small"
      variant="outlined"
      value={formatWithCommas(value)}
      onChange={handleChange}
      InputProps={{ readOnly: !canEdit }}
      disabled={!canEdit}
      inputProps={{ style: { textAlign: "center", padding: "5px 8px" }, inputMode: "numeric" }}
      sx={{
        padding: "8px",
        width: "100%",
        minWidth: 90,
        "& .MuiOutlinedInput-root": { borderRadius: "6px" },
        ...disabledFieldSx,
      }}
    />
  );
});

// ── Admin table: Marketer (rows) x Date (columns) ───────────────────────────
const AdminMatrixTable = ({ rows, dateRange, dateSendoutMap, canEdit, onCellChange }) => {
  const columns = useMemo(
    () => [
      {
        accessorKey: "CompanyName",
        header: "Marketer",
        enableColumnFilter: true,
        filterVariant: "text",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) =>
          row.original.isSendoutRow ? (
            <Typography sx={{ fontWeight: 700, color: "#1a3a5c", fontSize: "14px", padding: "8px" }}>
              Sendout
            </Typography>
          ) : (
            <Typography sx={{ fontWeight: 600, color: "#1a3a5c", fontSize: "14px", padding: "8px" }}>
              {row.original.CompanyName}
            </Typography>
          ),
      },
      ...dateRange.map((date) => ({
        accessorKey: date,
        header: date,
        enableColumnFilter: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell, row }) =>
          row.original.isSendoutRow ? (
            <Typography sx={{ fontWeight: 700, color: "#1a3a5c", fontSize: "14px", padding: "8px" }}>
              {formatSendout(cell.getValue())}
            </Typography>
          ) : (
            <EditableCell
              value={cell.getValue()}
              canEdit={canEdit}
              rowKey={row.original.CompanyName}
              colKey={date}
              onChange={onCellChange}
            />
          ),
      })),
    ],
    [dateRange, canEdit, onCellChange]
  );

  // ── Synthetic, always-first "Sendout" totals row ────────────────────────
  // Built here (not merged into the parent's `rows` state) so it never
  // leaks into onCellChange/save payloads — those only ever see real
  // marketer rows.
  const sendoutRow = useMemo(
    () => ({
      CompanyName: SENDOUT_ROW_ID,
      isSendoutRow: true,
      ...Object.fromEntries(dateRange.map((date) => [date, dateSendoutMap?.[date] ?? 0])),
    }),
    [dateRange, dateSendoutMap]
  );
  const tableData = useMemo(() => [sendoutRow, ...rows], [sendoutRow, rows]);

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    ...sharedTableOptions,
    enableColumnFilters: true,
    columnFilterDisplayMode: "popover",
    enableRowVirtualization: true,
    rowVirtualizerOptions: { overscan: 8 },
    getRowId: (row) => row.CompanyName,
    enableRowPinning: true,
    rowPinningDisplayMode: "top",
    // ── Hide MRT's built-in pin-toggle column ────────────────────────────
    // Pinning is applied programmatically (below), so the per-row pin
    // button UI isn't needed.
    displayColumnDefOptions: {
      "mrt-row-pin": {
        muiTableHeadCellProps: { sx: { display: "none" } },
        muiTableBodyCellProps: { sx: { display: "none" } },
      },
    },
    // ── Sendout row styling: light blue band, bold text, no interaction —
    // mirrors the CityGate total-row pattern (CityGateList.js) so both
    // tables' "total" rows read the same way.
    muiTableBodyRowProps: ({ row }) => {
      if (!row.original.isSendoutRow) return {};
      const { density } = table.getState();
      return {
        sx: {
          backgroundColor: "#DFEDFF !important",
          pointerEvents: "none",
          "& td": {
            color: "#1a3a5c !important",
            fontWeight: "bold !important",
            backgroundColor: "#DFEDFF !important",
          },
          "&:hover": { backgroundColor: "#DFEDFF !important" },
          "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: "#DFEDFF !important" },
          height: row.getIsPinned()
            ? `${density === "compact" ? 37 : density === "comfortable" ? 53 : 69}px`
            : undefined,
        },
      };
    },
  });

  useEffect(() => {
    table.setRowPinning({ top: [SENDOUT_ROW_ID] });
  }, [table]);

  return <MaterialReactTable table={table} />;
};

// ── Non-admin table: Date (rows) x single Value column ──────────────────────
const MarketerMatrixTable = ({ rows, canEdit, onCellChange }) => {
  const columns = useMemo(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell }) => (
          <Typography sx={{ fontWeight: 600, color: "#1a3a5c", fontSize: "14px", padding: "8px" }}>
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "sendout",
        header: "Sendout",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell }) => (
          <Typography sx={{ fontWeight: 600, color: "#1a3a5c", fontSize: "14px", padding: "8px" }}>
            {formatSendout(cell.getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: "value",
        header: "Planned Injections",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell, row }) => (
          <EditableCell
            value={cell.getValue()}
            canEdit={canEdit}
            rowKey={row.original.date}
            colKey="value"
            onChange={onCellChange}
          />
        ),
      },
    ],
    [canEdit, onCellChange]
  );

  const table = useMaterialReactTable({ columns, data: rows, ...sharedTableOptions });
  return <MaterialReactTable table={table} />;
};

const MatrixComplianceTable = ({
  isAdmin,
  rows,
  dateRange,
  dateSendoutMap,
  canEdit,
  onCellChange,
  marketerName,
}) => {
  if (isAdmin) {
    return (
      <AdminMatrixTable
        rows={rows}
        dateRange={dateRange}
        dateSendoutMap={dateSendoutMap}
        canEdit={canEdit}
        onCellChange={onCellChange}
      />
    );
  }

  return (
    <>
      {marketerName && (
        <Typography sx={{ fontWeight: 700, color: "#1a3a5c", mb: 1 }}>{marketerName}</Typography>
      )}
      <MarketerMatrixTable rows={rows} canEdit={canEdit} onCellChange={onCellChange} />
    </>
  );
};

export default MatrixComplianceTable;
